/* ts-pdp.js — Tail Squad harness product page interactions.
 * Vanilla JS, no jQuery, no new dependencies. Deferred.
 *
 * Deliberately does NOT reimplement add-to-cart, wishlist, or the deal
 * countdown: this theme already handles those globally —
 *   - Add to cart: theme.js delegates `form .btn-add-cart` clicks on
 *     `document`, AJAX-posts to /cart/add.js and reveals the existing
 *     cart dropdown. Our form just needs the same classes/fields.
 *   - Wishlist: theme.js's wslst() binds `.btn-wishlist[data-product-handle]`
 *     globally on page load.
 *   - Deal countdown: rendered through the theme's own 'product_deal'
 *     snippet/CSS/JS via the tailsquad.deal_ends_at metafield, so the
 *     ticking logic is the theme's proven code, not new code here.
 * Everything below is genuinely new UI this page introduces.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function formatMoney(cents, currencySymbol) {
    var v = (cents / 100);
    return currencySymbol + v.toLocaleString('en-IN', { maximumFractionDigits: v % 1 === 0 ? 0 : 2 });
  }

  ready(function () {
    document.querySelectorAll('[data-ts-main]').forEach(initMainProduct);
    document.querySelectorAll('[data-ts-explorer]').forEach(initExplorer);
    document.querySelectorAll('[data-ts-reels]').forEach(initReels);
    document.querySelectorAll('[data-ts-coupon]').forEach(initCoupon);
    document.querySelectorAll('[data-ts-recs]').forEach(initRecsGrid);
  });

  /* ================= Main product: gallery + variants + size chart + sticky bar ================= */
  function initMainProduct(root) {
    var currencySymbol = root.getAttribute('data-currency-symbol') || '₹';

    /* ---- Gallery: desktop thumbs ---- */
    var thumbs = root.querySelectorAll('[data-ts-thumb]');
    var stageImg = root.querySelector('[data-ts-stage-image]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
        var full = thumb.getAttribute('data-full');
        var alt = thumb.getAttribute('data-alt');
        if (stageImg && full) {
          stageImg.src = full;
          stageImg.alt = alt || stageImg.alt;
          stageImg.setAttribute('data-media-alt', (alt || '').toLowerCase());
        }
      });
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          var next = thumb.nextElementSibling;
          if (next) { next.focus(); next.click(); }
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          var prev = thumb.previousElementSibling;
          if (prev) { prev.focus(); prev.click(); }
        }
      });
    });

    /* ---- Variant data (from the real product, embedded server-side) ---- */
    var dataEl = root.querySelector('[data-ts-variant-json]');
    var variants = [];
    try { variants = dataEl ? JSON.parse(dataEl.textContent) : []; } catch (e) { variants = []; }

    var variantInput = root.querySelector('[data-ts-variant-input]');
    var priceNow = root.querySelector('[data-ts-price-now]');
    var priceWas = root.querySelector('[data-ts-price-was]');
    var priceOff = root.querySelector('[data-ts-price-off]');
    var priceSave = root.querySelector('[data-ts-price-save]');
    var saveBadge = root.querySelector('[data-ts-save-badge]');
    var fitBox = root.querySelector('[data-ts-fitbox]');
    var addBtn = root.querySelector('[data-ts-add-btn]');
    var buyNowPrice = root.querySelector('[data-ts-buynow-price]');
    var stickyBar = root.querySelector('[data-ts-sticky-bar]');
    var stickyPrice = root.querySelector('[data-ts-sticky-price]');
    var stickyMrp = root.querySelector('[data-ts-sticky-mrp]');
    var stickySelect = root.querySelector('[data-ts-sticky-select]');
    var stickyBuyBtn = root.querySelector('[data-ts-sticky-buy]');
    var qtyInput = root.querySelector('[data-ts-qty-input]');

    function findVariant(id) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return null;
    }

    function applyVariant(variant, pushUrl) {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (priceNow) priceNow.textContent = formatMoney(variant.price, currencySymbol);
      if (priceWas) { priceWas.hidden = !onSale; if (onSale) priceWas.textContent = formatMoney(variant.compare_at_price, currencySymbol); }
      if (priceOff) {
        priceOff.hidden = !onSale;
        if (onSale) {
          var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          priceOff.textContent = pct + '% OFF';
        }
      }
      if (priceSave) {
        priceSave.hidden = !onSale;
        if (onSale) priceSave.textContent = 'You save ' + formatMoney(variant.compare_at_price - variant.price, currencySymbol);
      }
      if (saveBadge) {
        saveBadge.hidden = !onSale;
        if (onSale) {
          var pct2 = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          saveBadge.textContent = 'Save ' + pct2 + '%';
        }
      }
      if (fitBox) {
        var parts = [];
        if (variant.ts_fit_weight) parts.push('<strong>Fits:</strong> ' + variant.ts_fit_weight);
        if (variant.ts_fit_chest) parts.push('<strong>Chest:</strong> ' + variant.ts_fit_chest);
        if (variant.ts_fit_neck) parts.push('<strong>Neck:</strong> ' + variant.ts_fit_neck);
        if (parts.length) { fitBox.innerHTML = parts.join(' &middot; '); fitBox.hidden = false; }
        else { fitBox.hidden = true; }
      }
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      if (buyNowPrice) buyNowPrice.textContent = formatMoney(variant.price, currencySymbol);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, currencySymbol);
      if (stickyMrp) {
        stickyMrp.hidden = !onSale;
        if (onSale) stickyMrp.textContent = formatMoney(variant.compare_at_price, currencySymbol);
      }
      if (stickySelect && variant.options && variant.options.length) {
        stickySelect.value = variant.options[0];
      }
      if (stickyBuyBtn) stickyBuyBtn.disabled = !variant.available;

      var qtyMax = root.querySelector('[data-ts-qty-max]');
      if (qtyMax) qtyMax.value = variant.inventory_quantity != null ? variant.inventory_quantity : '';

      if (variant.featured_media && stageImg && variant.featured_media.preview_image) {
        stageImg.src = variant.featured_media.preview_image.src || stageImg.src;
      }

      if (pushUrl && variant.id && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    /* ---- Option buttons: Size alone on the harness, Size + Color on the collars.
       Each button carries data-option-index so a click only resolves the FULL
       combination of currently selected values (not just the one option that
       was clicked) — otherwise picking a size after a color (or vice versa)
       would silently discard the other selection and add the wrong variant. */
    var optionButtons = root.querySelectorAll('[data-ts-size-btn]');
    var selectedValues = [];
    (function initSelectedValues() {
      var current = findVariant(variantInput ? variantInput.value : null);
      if (current && current.options) {
        selectedValues = current.options.slice();
      }
      optionButtons.forEach(function (b) {
        var idx = parseInt(b.getAttribute('data-option-index'), 10) || 0;
        if (selectedValues[idx] == null && b.getAttribute('aria-pressed') === 'true') {
          selectedValues[idx] = b.getAttribute('data-value');
        }
      });
    })();

    function findVariantByOptions(values) {
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].options || [];
        var isMatch = true;
        for (var j = 0; j < values.length; j++) {
          if (values[j] != null && opts[j] !== values[j]) { isMatch = false; break; }
        }
        if (isMatch) return variants[i];
      }
      return null;
    }

    optionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var idx = parseInt(btn.getAttribute('data-option-index'), 10) || 0;
        root.querySelectorAll('[data-ts-size-btn][data-option-index="' + idx + '"]').forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        selectedValues[idx] = btn.getAttribute('data-value');
        var variant = findVariantByOptions(selectedValues) || findVariant(btn.getAttribute('data-variant-id'));
        applyVariant(variant, true);
        track('ts_option_select', { index: idx, value: selectedValues[idx] });
      });
    });

    /* ---- Size chart: always opens as a popup window ---- */
    var sizeChartBtn = root.querySelector('[data-ts-size-chart-btn]');
    var sizeChartModal = root.querySelector('[data-ts-size-chart-modal]');
    function openSizeChartModal() {
      if (!sizeChartModal) return;
      sizeChartModal.hidden = false;
      document.body.style.overflow = 'hidden';
      var closeBtn = sizeChartModal.querySelector('[data-ts-modal-close]');
      if (closeBtn) closeBtn.focus();
    }
    function closeSizeChartModal() {
      if (!sizeChartModal) return;
      sizeChartModal.hidden = true;
      document.body.style.overflow = '';
      if (sizeChartBtn) sizeChartBtn.focus();
    }
    if (sizeChartBtn) {
      sizeChartBtn.addEventListener('click', openSizeChartModal);
    }
    if (sizeChartModal) {
      sizeChartModal.querySelectorAll('[data-ts-modal-close]').forEach(function (btn) {
        btn.addEventListener('click', closeSizeChartModal);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sizeChartModal.hidden) closeSizeChartModal();
      });
    }

    /* ---- Quantity stepper (native .quantity input is what the theme's cart AJAX reads) ---- */
    var qtyMinus = root.querySelector('[data-ts-qty-minus]');
    var qtyPlus = root.querySelector('[data-ts-qty-plus]');
    if (qtyInput && qtyMinus) {
      qtyMinus.addEventListener('click', function () {
        qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    if (qtyInput && qtyPlus) {
      qtyPlus.addEventListener('click', function () {
        qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /* ---- Sticky mobile bar: show once the real purchase row scrolls out of view ---- */
    if (stickyBar) {
      var purchaseRow = root.querySelector('[data-ts-purchase]');
      if (purchaseRow && 'IntersectionObserver' in window) {
        var stickyObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            stickyBar.classList.toggle('is-visible', !entry.isIntersecting && window.innerWidth < 990);
          });
        }, { threshold: 0 });
        stickyObserver.observe(purchaseRow);
      }

      /* Size select mirrors the main size buttons by forwarding to the matching button's own click handler */
      if (stickySelect) {
        stickySelect.addEventListener('change', function () {
          var escaped = window.CSS && CSS.escape ? CSS.escape(stickySelect.value) : stickySelect.value.replace(/"/g, '\\"');
          var match = root.querySelector('[data-ts-size-btn][data-option-index="0"][data-value="' + escaped + '"]');
          if (match && !match.disabled) match.click();
        });
      }

      /* Buy Now: hands off to the SAME Shiprocket "Fastrr" headless checkout
         widget the main purchase row uses (the `.shiprocket-headless`
         container inside the #ts-main-form), instead of hard-coding its own
         /cart/add.js + redirect to Shopify's native /checkout. That native
         redirect was the bug: on mobile it always sent shoppers to Shopify
         checkout even for the visitors the theme (see layout/theme.liquid's
         India/rest-of-world toggle) means to route through Shiprocket.
         Delegating the click to Fastrr's own injected button means this
         button follows whichever checkout that toggle already chose, and
         Fastrr handles the add-to-cart + checkout itself — exactly like
         clicking its button in the main purchase row would. Falls back to
         the old add+redirect behavior only when Shiprocket's widget isn't
         the active one (native-checkout visitors) or hasn't finished
         loading yet, so there's always a working button either way. */
      if (stickyBuyBtn) {
        stickyBuyBtn.addEventListener('click', function () {
          if (stickyBuyBtn.disabled) return;
          var id = variantInput ? variantInput.value : null;
          var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
          if (!id) return;

          var srContainer = document.querySelector('.shiprocket-headless[data-type="product"]');
          var srButton = srContainer && (
            srContainer.querySelector('.sr-headless-checkout button, .sr-headless-checkout a, .sr-headless-checkout [role="button"]') ||
            srContainer.querySelector('.sr-headless-checkout')
          );
          var srActive = !!(srContainer && srButton && window.getComputedStyle(srContainer).display !== 'none');

          track('ts_sticky_buy_now', { variant_id: id, qty: qty, via: srActive ? 'shiprocket' : 'shopify_checkout' });

          if (srActive) {
            srButton.click();
            return;
          }

          var original = stickyBuyBtn.textContent;
          stickyBuyBtn.disabled = true;
          stickyBuyBtn.textContent = 'Adding…';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: id, quantity: qty })
          }).then(function (res) {
            if (!res.ok) throw new Error('add failed');
            window.location.href = '/checkout';
          }).catch(function () {
            stickyBuyBtn.disabled = false;
            stickyBuyBtn.textContent = original;
            window.location.href = '/cart';
          });
        });
      }
    }

    /* ---- Analytics hooks (no-op unless gtag/fbq exist) ---- */
    var buyNowBtn = root.querySelector('[data-ts-buynow]');
    if (buyNowBtn) buyNowBtn.addEventListener('click', function () { track('ts_buy_now_click', {}); });
  }

  function track(eventName, detail) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, detail);
      if (typeof window.fbq === 'function') window.fbq('trackCustom', eventName, detail);
      document.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
    } catch (e) { /* analytics should never break the page */ }
  }

  /* ================= Hotspots + QR explainer ================= */
  function initExplorer(root) {
    var pins = root.querySelectorAll('[data-ts-pin]');
    var cardTitle = root.querySelector('[data-ts-hotspot-title]');
    var cardDesc = root.querySelector('[data-ts-hotspot-desc]');
    pins.forEach(function (pin) {
      pin.addEventListener('click', function () {
        pins.forEach(function (p) { p.classList.toggle('is-active', p === pin); });
        if (cardTitle) cardTitle.textContent = pin.getAttribute('data-title') || '';
        if (cardDesc) cardDesc.textContent = pin.getAttribute('data-desc') || '';
        track('ts_hotspot_tap', { label: pin.getAttribute('data-title') });
      });
    });
  }

  /* ================= Video reels ================= */
  function initReels(root) {
    var reels = root.querySelectorAll('[data-ts-reel]');
    reels.forEach(function (reel) {
      var playBtn = reel.querySelector('[data-ts-reel-play]');
      if (!playBtn) return;
      playBtn.addEventListener('click', function () {
        reels.forEach(function (r) {
          if (r !== reel) {
            r.classList.remove('is-playing');
            var v = r.querySelector('video');
            if (v) v.pause();
          }
        });
        var video = reel.querySelector('video[data-src]');
        if (video && !video.src) {
          video.src = video.getAttribute('data-src');
        }
        reel.classList.add('is-playing');
        if (video) { video.muted = true; video.controls = true; video.play().catch(function () {}); }
        track('ts_video_play', { caption: reel.getAttribute('data-caption') });
      });
    });
  }

  /* ================= Coupon copy ================= */
  function initCoupon(root) {
    var btn = root.querySelector('[data-ts-coupon-copy]');
    var code = root.querySelector('[data-ts-coupon-code]');
    if (!btn || !code) return;
    btn.addEventListener('click', function () {
      var text = code.textContent.trim();
      var done = function () {
        var original = btn.textContent;
        btn.textContent = 'Copied';
        track('ts_coupon_copy', { code: text });
        setTimeout(function () { btn.textContent = original; }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(done);
      } else {
        done();
      }
    });
  }

  /* ================= Product recommendation grids ================= */
  function initRecsGrid(root) {
    var url = root.getAttribute('data-url');
    var row = root.querySelector('[data-ts-recs-row]');
    if (!url || !row) return;
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('[data-ts-recs-row]');
        if (source && source.innerHTML.trim().length) {
          row.innerHTML = source.innerHTML;
        } else {
          root.style.display = 'none';
        }
      })
      .catch(function () { /* leave as-is, no error shown to shoppers */ });
  }
})();